// Equalize — equalize histogram distribution.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface EqualizeParams {
  basedOnSelection: boolean;
  perChannel: boolean;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const Equalize: Filter<EqualizeParams> = {
  id: "equalize",
  displayName: "Equalize",
  defaultParams: {
    basedOnSelection: false,
    perChannel: false,
  },

  apply(
    input: PixelBuffer,
    params: EqualizeParams,
    selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    if (params.perChannel) {
      for (let c = 0; c < 3; c++) {
        const hist = new Int32Array(256);
        let count = 0;

        for (let i = 0; i < n; i++) {
          if (selection && selection.contains(i % w, Math.floor(i / w)) === 0) continue;
          const val = src[i * 4 + c]!;
          hist[val] = (hist[val] ?? 0) + 1;
          count++;
        }

        if (count === 0) continue;

        const cdf = new Int32Array(256);
        cdf[0] = hist[0]!;
        for (let i = 1; i < 256; i++) {
          cdf[i] = cdf[i - 1]! + hist[i]!;
        }

        const lut = new Uint8Array(256);
        let minCdf = 0;
        for (let i = 0; i < 256; i++) {
          if (cdf[i]! > 0) { minCdf = cdf[i]!; break; }
        }
        const scale = count > minCdf ? 255 / (count - minCdf) : 0;
        for (let i = 0; i < 256; i++) {
          lut[i] = clamp8(Math.round((cdf[i]! - minCdf) * scale));
        }

        for (let i = 0; i < n; i++) {
          if (selection && selection.contains(i % w, Math.floor(i / w)) === 0) {
            dst[i * 4 + c] = src[i * 4 + c]!;
          } else {
            const val = src[i * 4 + c]!;
            dst[i * 4 + c] = lut[val] ?? 0;
          }
        }
      }
      for (let i = 0; i < n; i++) {
        dst[i * 4 + 3] = src[i * 4 + 3]!;
      }
    } else {
      const hist = new Int32Array(256);
      let count = 0;

      for (let i = 0; i < n; i++) {
        if (selection && selection.contains(i % w, Math.floor(i / w)) === 0) continue;
        const o = i * 4;
        const r = src[o]!;
        const g = src[o + 1]!;
        const b = src[o + 2]!;
        const lum = Math.round(0.2126 * r + 0.7152 * g + 0.0722 * b);
        hist[lum] = (hist[lum] ?? 0) + 1;
        count++;
      }

      if (count === 0) {
        dst.set(src);
        return out;
      }

      const cdf = new Int32Array(256);
      cdf[0] = hist[0]!;
      for (let i = 1; i < 256; i++) {
        cdf[i] = cdf[i - 1]! + hist[i]!;
      }

      const lut = new Uint8Array(256);
      let minCdf = 0;
      for (let i = 0; i < 256; i++) {
        if (cdf[i]! > 0) { minCdf = cdf[i]!; break; }
      }
      const scale = count > minCdf ? 255 / (count - minCdf) : 0;
      for (let i = 0; i < 256; i++) {
        lut[i] = clamp8(Math.round((cdf[i]! - minCdf) * scale));
      }

      for (let i = 0; i < n; i++) {
        const o = i * 4;
        if (selection && selection.contains(i % w, Math.floor(i / w)) === 0) {
          dst[o] = src[o]!;
          dst[o + 1] = src[o + 1]!;
          dst[o + 2] = src[o + 2]!;
        } else {
          const r = src[o]!;
          const g = src[o + 1]!;
          const b = src[o + 2]!;
          const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
          const newLum = lut[Math.round(lum)]!;
          const ratio = lum > 0 ? newLum / lum : 0;
          dst[o] = clamp8(Math.round(r * ratio));
          dst[o + 1] = clamp8(Math.round(g * ratio));
          dst[o + 2] = clamp8(Math.round(b * ratio));
        }
        dst[o + 3] = src[o + 3]!;
      }
    }

    return out;
  },
};
